import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/contexts/auth'
import { supabase } from '@/lib/supabase'

export interface GenreData {
  genre_id: string
  genre_name: string
  genre_color: string
  total_books: number
  read_books: number
  percentage: number
}

export interface UseTopGenresReturn {
  data: GenreData[]
  isLoading: boolean
  error: Error | null
}

/**
 * Hook for top genres data
 * Uses PostgreSQL stored function for maximum performance
 */
export function useTopGenres(limit = 8): UseTopGenresReturn {
  const { user } = useAuth()

  const { data, isLoading, error } = useQuery({
    queryKey: ['top-genres', user?.id, limit],
    queryFn: async (): Promise<GenreData[]> => {
      if (!user?.id) throw new Error('User not authenticated')

      // Uses PostgreSQL stored function for optimization
      const { data, error } = await supabase.rpc('get_top_genres', {
        user_uuid: user.id,
        genre_limit: limit,
      })

      if (error) {
        console.error('Top genres error:', error)
        throw new Error(`Failed to fetch top genres: ${error.message}`)
      }

      return data || []
    },
    enabled: !!user?.id,
    staleTime: 10 * 60 * 1000, // 10 minutes - genres don't change frequently
    gcTime: 30 * 60 * 1000, // 30 minutes in cache
    retry: 2,
  })

  return {
    data: data || [],
    isLoading,
    error: error as Error | null,
  }
}
